import type {
  AddToCartItem,
  RemoveCartItem,
  AddToCartResponse,
  AddToCartBatchResponse,
} from './types';
import { CartClientId } from './constants';
import * as endpoints from './endpoints';
import { formPost, jsonPost } from 'fetch_utils';
import {
  processItemBeforeSubmit,
  groupItemsByShopId,
  getResetGroupFromItems,
} from './utils';

export type ApiResponse<T> = {
  response?: T;
  data?: any;
  error_response?: Record<string, any>;
  error?: number;
  error_msg?: string;
};

export type ChannelExclusiveInfoInput = {
  source_id: number;
  token: string;
};

// v2/4 add to cart API
export function addItemToCart(
  item: AddToCartItem,
  options?: {
    cart_client_id?: CartClientId;
    headers?: Record<string, any>;
  }
): Promise<ApiResponse<AddToCartResponse>> {
  const body = processItemBeforeSubmit(item);
  const { headers, ...rest } = options ?? {};
  Object.assign(body, rest);

  return jsonPost(endpoints.URL_ADD_TO_CART_V4, body, {
    headers,
  });
}

export function addItemToCartBatch(
  items: Array<AddToCartItem>,
  options?: {
    // turning on this flag means all groups these items belong to
    // will be replaced with item info we provide here.
    shouldReset?: boolean;
    clientSource?: number;
    channelExclusiveInfo?: ChannelExclusiveInfoInput;
    cart_client_id?: CartClientId;
  }
): Promise<ApiResponse<AddToCartBatchResponse>> {
  const { shouldReset, clientSource, channelExclusiveInfo, ...rest } =
    options || {};
  const processedItems = items.map(processItemBeforeSubmit).map(item =>
    shouldReset
      ? {
          ...item,
          donot_add_quantity: false,
        }
      : item
  );
  const groupedItemList = groupItemsByShopId(processedItems);
  const body: any = {
    // group by shop_id according to WSA doc.
    orders: JSON.stringify(groupedItemList),
    ...(!shouldReset
      ? null
      : {
          reset_groups: JSON.stringify(getResetGroupFromItems(items)),
        }),
    ...(clientSource != null
      ? {
          client_source: clientSource,
        }
      : null),
    ...(channelExclusiveInfo != null
      ? {
          channel_exclusive_info: channelExclusiveInfo,
        }
      : null),
  };
  Object.assign(body, rest);

  return jsonPost(endpoints.URL_ADD_TO_CART_BATCH_V4, body);
}

export function removeItemFromCart(item: RemoveCartItem) {
  return formPost(
    endpoints.URL_REMOVE_FROM_CART,
    processItemBeforeSubmit(item)
  );
}
